import { CreatedFilters } from "@/services/filters/filters";
import { IColumnFilter, IFilter } from "@/types";
import { filterClass } from "@/services/filters/filterClass";
import { getAffiliateSearch, getUserSearch } from "@/api/user";
import { translateAffiliateStatus } from "@/utils/translate";
import { getEmployeeMethods } from "@/api/payout";
import { ITableHeader, showServerError } from "@/utils";
import { CreatedColumns } from "@/services/columns/columns";
import { AffiliateInvoiceInfo, IMyLeadgidInvoice } from "@/api/types/payout";
import SvgMasterInvoice from "@/assets/icons/master-invoice.svg";
import { IListItem, InfoCards, usePrepareInfoCards } from "@/components-UI/list/types";
import store from "@/store";
import { computed } from "vue";
import { ITdRoute } from "@/components/base/TableUI/types";

export class AffiliateInvoicesFilters extends CreatedFilters {
    constructor() {
        super();

        (async () => await this.setEmployeeMethods())();
    }

    getFilters(): IFilter[] {
        const affiliate = store.getters['GET_CURRENT_AFFILIATE'];
        const isAffiliate = this.checkRoute('affiliate');
        const isMaster = store.state.isMasterAffiliate;

        return [
            new filterClass.ComboboxArray({
                id: 'invoice_ids',
                label: 'ID счёта',
                select: [],
                checkMultiPaste: true,
                multiPaste: true,
            }),
            new filterClass.Autocomplete({
                id: 'master_ids',
                apiMethod: getAffiliateSearch,
                label: 'Мастер-аккаунт',
                itemValue: 'id',
                itemText: 'name',
                select: isAffiliate && isMaster ? [affiliate] : [],
                defaultValue: isAffiliate && isMaster ? [affiliate] : [],
                items: [],
                multiple: true,
                multiPaste: true,
                alsoSearchBy: 'email',
                isHidden: isAffiliate,
                getValueForSubmit(filter: IFilter) {
                    return {
                        master_account_id: filter.select.map((item) => item.id),
                    };
                },
            }),
            new filterClass.Autocomplete({
                id: 'affiliate_id',
                apiMethod: getAffiliateSearch,
                label: 'Партнёр',
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name} ${i.email}`,
                select: isAffiliate && !isMaster ? [affiliate] : [],
                defaultValue: isAffiliate && !isMaster ? [affiliate] : [],
                items: [],
                multiple: true,
                multiPaste: true,
                isHidden: isAffiliate && !isMaster,
                getValueForSubmit(filter: IFilter) {
                    return {
                        [filter.id]: filter.select.map((item) => item.id),
                    };
                },
            }),
            new filterClass.Multiselect({
                id: 'currency',
                label: 'Валюта',
                noDataText: 'Нет выбранной валюты',
                items: [],
                itemValue: 'code',
                itemText: 'code',
                type: 'string',
                isCurrencyCatalogue: true,
            }),
            new filterClass.Multiselect({
                id: 'paid_status',
                type: 'string',
                label: 'Статус',
                items: [
                    { id: 'paid' },
                    { id: 'not_paid' },
                    { id: 'partial_paid' },
                ],
                select: [],
                template: (i) => translateAffiliateStatus(i.id).text,
                itemText: (i) => translateAffiliateStatus(i.id).text,
                itemValue: 'id',
            }),
            new filterClass.Autocomplete({
                id: 'manager_id',
                apiMethod: getUserSearch,
                label: 'Менеджер',
                itemValue: 'id',
                itemText: 'name',
                select: '',
                items: [],
                remove: true,
                multiple: false,
                isHidden: isAffiliate,
                pinCurrentUser: true,
                getValueForSubmit(filter: IFilter) {
                    return {
                        [filter.id]: filter.select.id,
                    };
                },
            }),
            new filterClass.Multiselect({
                id: 'current_method_ids',
                type: 'string',
                label: 'Текущий способ выплаты',
                noDataText: 'Нет выбранного способа выплаты',
                select: [],
                items: [],
                itemValue: 'id',
                isHidden: isAffiliate,
                itemText: (i) => i.name.ru,
                template: (i) => i.name.ru,
            }),
            new filterClass.Multiselect({
                // WARNING: Не уверен, что ничего не поломаю т.к надо срочно поменять позже удалю  / paid_by_payment_method_ids -> method_invoice_was_paid_ids
                id: 'paid_by_payment_method_ids',
                type: 'string',
                label: 'Способ выплаты',
                noDataText: 'Нет выбранного способа выплаты',
                select: [],
                items: [],
                itemValue: 'id',
                itemText: (i) => i.name.ru,
                template: (i) => i.name.ru,
            }),
            new filterClass.Datepicker({
                id: 'period',
                label: 'Период оплаты',
                pickerType: 'date',
                select: [],
                range: true,
                clearable: true,
                allowedDates: () => ({ start: null, end: new Date(), days: [1, 15] }),
            }),
            new filterClass.Checkbox({
                label: 'Мастер-счёт',
                id: 'is_master',
                select: [],
                itemText: 'true',
                type: 'string',
            }),
        ];
    }

    async setEmployeeMethods(): Promise<void> {
        try {
            const currentMethodFilter = this.filters.find(f => f.id === 'current_method_ids')!;
            // WARNING: Не уверен, что ничего не поломаю т.к надо срочно поменять позже удалю  / paid_by_payment_method_ids -> method_invoice_was_paid_ids
            const wasPaidFilter = this.filters.find(f => f.id === 'paid_by_payment_method_ids')!;
            if (currentMethodFilter?.items?.length! > 0 && wasPaidFilter?.items?.length!) return;
            const { data } = await getEmployeeMethods();
            wasPaidFilter.items = data;
            currentMethodFilter.items = data;
        } catch (err) {
            showServerError(err, 'Список способов выплат не загружен');
        }
    }
}

export class AffiliateInvoicesColumns extends CreatedColumns {
    constructor() {
        super();
    }

    getColumns(): IColumnFilter[] {
        return [
            {
                value: false,
                name: 'Счета',
                items: [
                    { title: 'Партнёр', value: 'affiliate_id' },
                    { title: 'Менеджер', value: 'manager_id' },
                    { title: 'Мастер-аккаунт', value: 'master_id' },
                    { title: 'Доход', value: 'total_revenue' },
                    { title: 'Выплата', value: 'total_amount' },
                    { title: 'Выплачено', value: 'partials_amount' },
                    { title: 'Остаток', value: 'rest_amount' },
                    { title: 'Прибыль', value: 'total_profit' },
                    { title: 'Период оплаты', value: 'pay_period' },
                    { title: 'Дата создания', value: 'created_at' },
                    { title: 'Дата обновления', value: 'updated_at' },
                    { title: 'Статус', value: 'paid_status' },
                ],
            },
            {
                id: 'payment',
                value: false,
                name: 'Куда была оплата',
                items: [
                    { title: 'Способ выплаты', value: 'paid_by_payment_method' },
                    { title: 'Реквизиты', value: 'paid_by_requisites' },
                ],
            },
            {
                id: 'current_payment',
                value: false,
                name: 'Текущий способ выплаты',
                items: [
                    { title: 'Способ выплаты', value: 'current_payment_method' },
                    { title: 'Реквизиты', value: 'current_requisite' },
                ],
            },
        ];
    }
}

export const AffiliateInvoicesHeaders: ITableHeader[] = [
    { text: 'ID счёта', value: 'id', align: 'center', width: '134px', fixed: true },
    { text: 'Партнёр', value: 'affiliate_id', align: 'left', width: 'max-content' },
    { text: 'Менеджер', value: 'manager_id', width: '130px' },
    { text: 'Мастер-аккаунт', value: 'master_id', width: '130px' },
    { text: 'Валюта', value: 'currency', fixed: true, width: '130px' },
    { text: 'Доход', value: 'total_revenue', align: 'right', width: '130px' },
    { text: 'Выплата', value: 'total_amount', align: 'right', width: '130px' },
    { text: 'Выплачено', value: 'partials_amount', align: 'right', width: 'max-content' },
    { text: 'Остаток', value: 'rest_amount', align: 'right', width: '130px' },
    { text: 'Прибыль', value: 'total_profit', align: 'right', width: '130px' },
    {
        section: 'Куда была оплата',
        text: 'Способ выплаты',
        value: 'paid_by_payment_method',
        sectionDescription: 'Основной способ выплаты партнёра на момент оплаты счёта',
        width: 'max-content',
    },
    { section: 'Куда была оплата', text: 'Реквизиты', value: 'paid_by_requisites', width: 'max-content' },
    { section: 'Текущий способ выплаты', text: 'Способ выплаты', value: 'current_payment_method', width: 'max-content' },
    { section: 'Текущий способ выплаты', text: 'Реквизиты', value: 'current_requisite', width: 'max-content' },
    { text: 'Период оплаты', value: 'pay_period', align: 'right', width: '130px' },
    { text: 'Дата создания', value: 'created_at', align: 'right', width: '90px' },
    { text: 'Дата обновления', value: 'updated_at', align: 'right', width: '90px' },
    { text: 'Статус', value: 'paid_status', width: '130px' },
];

export const AffiliateInvoicesRoutes: ITdRoute[] = [
    {
        routeParam: 'id',
        slot: 'item.id',
        routerName: 'affiliate-invoice',
        text: 'id',
        asideIcon: SvgMasterInvoice,
        showIcon: (item: IMyLeadgidInvoice): boolean => !!item.is_master,
        tooltip: (item: IMyLeadgidInvoice): string => item.is_master ? 'Мастер-счёт' : '',
        tooltipClass: 'base-tooltip',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'affiliate_id',
        slot: 'item.affiliate_id',
        routerName: 'affiliate',
        text: 'affiliate_id',
        template: (item: IMyLeadgidInvoice): string | number => item.affiliate ? `${item.affiliate_id || ''} ${item.affiliate || ''}` : item.affiliate_id || '',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'master_id',
        slot: 'item.master_id',
        routerName: 'affiliate',
        text: 'master_id',
    },
];

export const AffiliateInvoicesNumberFormatting = [
    {
        slot: 'item.total_revenue',
        key: 'total_revenue',
        isMono: true,
    },
    {
        slot: 'item.total_amount',
        key: 'total_amount',
        isMono: true,
    },
    {
        slot: 'item.partials_amount',
        key: 'partials_amount',
        isMono: true,
    },
    {
        slot: 'item.total_profit',
        key: 'total_profit',
        isMono: true,
    },
    {
        slot: 'item.rest_amount',
        key: 'rest_amount',
        isMono: true,
    },
];

export const AffiliateInvoicesTimeData = [
    {
        slot: 'item.created_at',
        key: 'created_at',
        isLine: false,
    },
    {
        slot: 'item.updated_at',
        key: 'updated_at',
        isLine: false,
    },
];

type InvoiceItem = IListItem<AffiliateInvoiceInfo>
type InvoiceCardName = 'info' | 'financial';
export type AffiliateInvoiceCards = {
    [key in InvoiceCardName]: InvoiceItem[]
}

export const useAffiliateInvoiceSingle = (data: AffiliateInvoiceInfo): { getCards: () => InfoCards<AffiliateInvoiceInfo> } => {
    const getCurrencySymbol = computed(() => {
        const getter = store.getters['catalogueModule/GET_CURRENT_CURRENCY_ICON'];
        return getter(data.currency);
    });
    const getRestColor = computed(() => data.rest === 0 ? 'green' : 'red');

    const info: InvoiceItem[] = [
        {
            itemType: 'route',
            label: 'Партнёр',
            routeName: 'affiliate',
            routeText: 'affiliate_id',
            valueKey: 'affiliate_id',
            value: '',
        },
        {
            itemType: 'date',
            label: 'Период оплаты',
            valueKey: 'payment_period',
            value: '',
            dateFormat: 'dd MMMM yyyy',
        },
        {
            itemType: 'text',
            label: 'Email',
            valueKey: 'email',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Способ выплаты',
            valueKey: 'payment_method',
            value: '',
            hint: 'Основной способ выплаты партнёра на момент оплаты счёта',
        },
        {
            itemType: 'text',
            label: 'Менеджер',
            valueKey: 'manager',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Реквизиты',
            valueKey: 'requisite',
            value: '',
            hint: 'Реквизиты партнёра на момент оплаты счёта',
        },
        {
            itemType: 'text',
            label: 'Телефон',
            valueKey: 'phone',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Текущий сп.вып.',
            valueKey: 'current_payment_method',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Валюта',
            valueKey: 'currency',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Текущие реквизиты',
            valueKey: 'current_requisite',
            value: '',
        },
    ];

    const financial: InvoiceItem[] = [
        {
            itemType: 'number',
            label: 'Доход',
            valueKey: 'revenue',
            valueSlot: 'revenue',
            symbol: getCurrencySymbol.value,
            value: '',
        },
        {
            itemType: 'number',
            label: 'Выплата',
            valueKey: 'amount',
            valueSlot: 'amount',
            symbol: getCurrencySymbol.value,
            value: '',
        },
        {
            itemType: 'number',
            label: 'Прибыль',
            valueKey: 'profit',
            valueSlot: 'profit',
            symbol: getCurrencySymbol.value,
            value: '',
        },
        {
            itemType: 'number',
            label: 'Маржинальность',
            valueKey: 'margin',
            symbol: '%',
            value: '',
        },
        {
            itemType: 'number',
            label: 'Уже выплачено',
            valueKey: 'paid',
            valueSlot: 'paid',
            symbol: getCurrencySymbol.value,
            value: '',
        },
        {
            itemType: 'number',
            label: 'Остаток',
            valueKey: 'rest',
            valueSlot: 'rest',
            symbol: getCurrencySymbol.value,
            value: '',
            color: getRestColor.value,
            labelColor: getRestColor.value,
        },
    ];

    const cards: AffiliateInvoiceCards = {
        info,
        financial,
    };

    const getCards = () => {
        return usePrepareInfoCards<AffiliateInvoiceInfo>(cards, data);
    };

    return { getCards };
};
